import React, { useRef } from 'react'

import { LegacyIcons } from 'components/elements/Icon'

import { useDebouncedResizeObserver } from 'hooks/resizeObservers'

import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import Icon from 'components/elements/Icon'
import CustomIcon, { CustomIcons } from 'components/elements/CustomIcon'

import styles from './tabButtons.module.scss'

type Option = {
  label: React.ReactNode
  icon?: LegacyIcons
  customIcon?: CustomIcons
  selected?: boolean
  onClick: (e?: React.MouseEvent) => void
  hidden?: boolean
  disabled?: boolean
  className?: string
  title?: string
}

type Props = {
  className?: string
  optionsClassName?: string
  noHideOverflow?: boolean
} & ({ children: React.ReactNode; options?: never } | { options?: Option[]; children?: never })

const TabContainer = ({
  className = '',
  optionsClassName = '',
  children = null,
  noHideOverflow = false,
  options = [],
}: Props) => {
  const { ref: container, width = 1 } = useDebouncedResizeObserver<HTMLDivElement>(200)

  let totalNonHiddenOptions = options.filter(o => !o.hidden).length
  let visualOptions = options
  let overflowOptions = new Array<Option>()

  if (totalNonHiddenOptions > 5 && !noHideOverflow) {
    visualOptions = options.filter((_o, i) => i < 5)
    overflowOptions = options.filter((_o, i) => i >= 5)
  }

  if (totalNonHiddenOptions > 3 && width < 1000 && !noHideOverflow) {
    const activeOption = options.find(o => o.selected) || options[0]

    const firstOption = options[0]
    const secondOption = activeOption?.label === firstOption.label ? options[1] : activeOption

    visualOptions = [firstOption, secondOption]

    const seenIds = visualOptions.map(o => o.label)

    overflowOptions = options.filter(o => !seenIds.includes(o.label))
  }

  return (
    <div className={`${styles.container} ${className}`} ref={container}>
      {children}
      {visualOptions.map((option, index) => (
        <TabButton
          disabled={option.disabled}
          key={index}
          selected={option.selected}
          onClick={option.onClick}
          icon={option.icon}
          customIcon={option.customIcon}
          hidden={option.hidden}
          title={option.title}
          className={`${option.className} ${optionsClassName}`}>
          {option.label}
        </TabButton>
      ))}
      {overflowOptions.length > 0 && (
        <ArchidektDropdown options={overflowOptions} menuClassName={styles.overflowOptionsMenu}>
          <TabButton onClick={() => null} className={`${styles.overflowOptionsTrigger} ${optionsClassName}`}>
            More options <Icon name="chevron circle down" />
          </TabButton>
        </ArchidektDropdown>
      )}
    </div>
  )
}

export default TabContainer

type ButtonProps = {
  selected?: boolean
  children?: React.ReactNode
  onClick: (e?: React.MouseEvent) => void
  className?: string
  icon?: LegacyIcons
  customIcon?: CustomIcons
  disabled?: boolean
  hidden?: boolean
  title?: string
}

export const TabButton = ({
  className = '',
  selected = false,
  children = null,
  onClick,
  icon,
  customIcon,
  disabled,
  hidden,
  title,
}: ButtonProps) => {
  if (hidden) return null

  return (
    <button
      title={title}
      disabled={disabled}
      className={`${styles.button} ${selected ? styles.selected : ''} ${className}`}
      onClick={onClick}>
      {icon && <Icon name={icon} />}
      {customIcon && <CustomIcon name={customIcon} />}
      {children}
    </button>
  )
}
